import React, { useState } from "react";
import { ScrollView, Text, View, TouchableOpacity } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import Loader from "@/elements/components/shared/loader";
import { format } from "date-fns";

function EarningsScreen() {
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());

  const { data: earnings, isLoading: isEarningsLoading } = useQuery(
    convexQuery(api.tutor.payouts.queries.getMyEarnings, {})
  );

  const { data: payouts, isLoading: isPayoutsLoading } = useQuery(
    convexQuery(api.tutor.payouts.queries.getMyPayouts, {})
  );

  if (isEarningsLoading || isPayoutsLoading) {
    return <Loader />;
  }

  const formatAmount = (
    payableAmount: number,
    currency: string,
    debitOffsetAmount = 0,
  ) => {
    const payable = Math.abs(payableAmount).toFixed(2);
    const currencyLabel = currency.toUpperCase();
    if (debitOffsetAmount > 0) {
      return `${payable}(-${debitOffsetAmount.toFixed(2)}) ${currencyLabel}`;
    }
    if (payableAmount < 0) return `-${payable} ${currencyLabel}`;
    return `${payable} ${currencyLabel}`;
  };

  const getAmountColor = (payableAmount: number, status: string, debitOffsetAmount = 0) => {
    if (payableAmount < 0) return "text-red-600";
    if (debitOffsetAmount > 0 && payableAmount === 0) return "text-orange-500";
    return status === "completed" ? "text-teal-600" : "text-orange-500";
  };

  const getStatusColor = (status: string) => {
    return status === "completed" ? "text-teal-600" : "text-orange-500";
  };

  const togglePayout = (payoutId: string) => {
    setExpandedPayouts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(payoutId)) {
        newSet.delete(payoutId);
      } else {
        newSet.add(payoutId);
      }
      return newSet;
    });
  };

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-4">
            <View className="h-3" />
            
            {/* Header */}
            <View className="gap-1">
              <Text className="font-zilla-slab-medium font-medium text-[32px] text-gray-800 leading-[1.2]">
                Earnings
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-gray-800 leading-[1.5]">
                Track your earnings and payout history
              </Text>
            </View>
            
            <View className="h-8" />
            
            {/* Total Earnings Card */}
            <View className="bg-teachLoop-creamDark rounded-2xl py-6 px-4">
              <Text className="font-geologica-medium font-medium text-[20px] text-gray-800 leading-[1.5] mb-2">
                Total earnings so far
              </Text>
              <Text className="font-zilla-slab-medium font-medium text-[36px] text-teal-600 leading-[1.2]">
                {earnings ? formatAmount(earnings.totalAmount, earnings.currency) : "0.00 AED"}
              </Text>
            </View>
            
            <View className="h-8" />
            
            {/* Payouts Table */}
            <Text className="font-geologica-medium font-medium text-[20px] text-gray-800 leading-[1.5] mb-4">
              Payout History
            </Text>
            
            {payouts && payouts.items.length > 0 ? (
              <View className="gap-3">
                {payouts.items.map((payout, index) => {
                  const isExpanded = expandedPayouts.has(payout._id);
                  const hasMultipleSessions = payout.sessionCount > 1;

                  return (
                    <View
                      key={index}
                      className="bg-white rounded-2xl p-4 border border-gray-100"
                    >
                      <TouchableOpacity
                        onPress={() => hasMultipleSessions && togglePayout(payout._id)}
                        disabled={!hasMultipleSessions}
                      >
                        <View className="flex-row justify-between items-start mb-2">
                          <View className="flex-1">
                            <Text className="font-geologica-medium font-medium text-[16px] text-gray-800 leading-[1.5]">
                              {payout.description}
                            </Text>
                            {hasMultipleSessions && (
                              <Text className="font-geologica-regular font-normal text-[14px] text-teal-600 leading-[1.5] mt-1">
                                {payout.sessionCount} sessions • Tap to {isExpanded ? 'collapse' : 'expand'}
                              </Text>
                            )}
                            <Text className="font-geologica-regular font-normal text-[14px] text-gray-600 leading-[1.5] mt-1">
                              Created: {format(new Date(payout._creationTime), "MMM dd, yyyy")}
                            </Text>
                            {payout.completedAt && (
                              <Text className="font-geologica-regular font-normal text-[14px] text-gray-600 leading-[1.5]">
                                Completed: {format(new Date(payout.completedAt), "MMM dd, yyyy")}
                              </Text>
                            )}
                          </View>
                          <View className="items-end">
                            <Text className={`font-geologica-medium font-medium text-[18px] leading-[1.5] ${getAmountColor(payout.payableAmount ?? payout.amount, payout.status, payout.debitOffsetAmount)}`}>
                              {formatAmount(
                                payout.payableAmount ?? payout.amount,
                                payout.currency,
                                payout.debitOffsetAmount,
                              )}
                            </Text>
                            {payout.debitOffsetAmount > 0 && (
                              <Text className="font-geologica-regular font-normal text-[12px] text-gray-500 leading-[1.5] mt-1 text-right">
                                After debit of {payout.debitOffsetAmount.toFixed(2)} {payout.currency.toUpperCase()}
                              </Text>
                            )}
                            <View className={`px-2 py-1 rounded-md mt-1 ${
                              payout.status === "completed" ? "bg-teal-100" : "bg-orange-100"
                            }`}>
                              <Text className={`font-geologica-medium font-medium text-xs leading-[1.5] uppercase ${getStatusColor(payout.status)}`}>
                                {payout.status}
                              </Text>
                            </View>
                            {hasMultipleSessions && (
                              <View className="mt-2">
                                <Text className="font-geologica-regular font-normal text-[16px] text-gray-500 leading-[1.5]">
                                  {isExpanded ? "▲" : "▼"}
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      </TouchableOpacity>

                      {isExpanded && payout.sessions && payout.sessions.length > 0 && (
                        <View className="mt-4 pt-4 border-t border-gray-100">
                          <Text className="font-geologica-medium font-medium text-[14px] text-gray-800 leading-[1.5] mb-3">
                            Sessions in this payout:
                          </Text>
                          <View className="gap-2">
                            {payout.sessions.map((session: any, sessionIndex: number) => (
                              <View
                                key={sessionIndex}
                                className="bg-gray-50 rounded-lg p-3"
                              >
                                <View className="flex-row justify-between items-start">
                                  <View className="flex-1">
                                    <Text className="font-geologica-medium font-medium text-[14px] text-gray-800 leading-[1.5]">
                                      {session.subjectName} with {session.studentName}
                                      {session.parentName ? ` · parent ${session.parentName}` : ""}
                                    </Text>
                                    <Text className="font-geologica-regular font-normal text-[12px] text-gray-600 leading-[1.5] mt-1">
                                      {format(new Date(session.startTime), "MMM dd, yyyy")} • {session.deliveryMethod === "in_person" ? "In-person" : "Online"}
                                    </Text>
                                  </View>
                                  <Text className="font-geologica-medium font-medium text-[14px] text-gray-800 leading-[1.5]">
                                    {formatAmount(session.amount, payout.currency)}
                                  </Text>
                                </View>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : (
              <View className="bg-white rounded-2xl p-6 items-center">
                <Text className="font-geologica-regular font-normal text-[16px] text-gray-600 leading-[1.5] text-center">
                  No payouts yet. Your earnings will appear here once you start teaching.
                </Text>
              </View>
            )}
            
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default EarningsScreen;